import * as React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";

import { Text } from "src/library/components/Text";
import { formatNumber } from "src/library/utils";
import { Tooltip } from "src/library/components/Tooltip";
import {
  formatPrice,
  formatShortPrice
} from "src/core/prices/utils";
import { minutesToHHMM } from "src/library/utils/time-helpers/time-helpers";

export interface NumberWidgetProps {
  displayData: {
    value: string | number;
    total?: number;
    data?: NumberWidgetTableData[]
  };
  units: string;
  widgetId: number | string;
  valueType?: "text" | "number" | "price" | "time";
  headers?: string[];
  fontSize: string;
  hideToolTip?: "header" | "td" | boolean;
}

interface NumberWidgetTableData {
  group: string;
  value: string | number;
  total?: number;
}

// Renders a number widget with the relevant data
export const NumberWidget: React.ReactFCWithChildren<NumberWidgetProps> = ({
  displayData,
  units,
  headers,
  fontSize,
  valueType,
  widgetId,
  hideToolTip = false
}) => {
  // Based on the value type, if provided, format the item value
  const formatValue = React.useCallback((value: unknown) => {
    switch (valueType) {
      case "text": {
        return typeof value === "string" ? _.startCase(value.replace(/[_-]/g, " ")) : value;
      }
      case "number": {
        return typeof value === "number" || typeof value === "string" ? formatNumber(value, "short") : value;
      }
      case "price": {
        return typeof value === "number" ? formatShortPrice(value) : value;
      }
      case "time": {
        return typeof value === "number" ? minutesToHHMM(value, "short") : value;
      }
      default:
        return value;
    }
  }, [ valueType ]);

  // Based on the value type, if provided, format the tooltip value
  const formatTooltipValue = React.useCallback((value: unknown) => {
    switch (valueType) {
      case "price": {
        return typeof value === "number" ? formatPrice(value, 2) : value;
      }
      case "time": {
        return typeof value === "number" ? minutesToHHMM(value, "long") : value;
      }
      case "number": {
        return typeof value === "number" || typeof value === "string" ? formatNumber(value) : value;
      }
      default:
        return value;
    }
  }, [ valueType ]);

  return (
    <>
      <StyledNumbersWrapper>
        {hideToolTip === true || hideToolTip !== "header" && (
          <Tooltip
            id={`${widgetId}-header-value-tool-tip`}
            type="dark"
            place="top"
            getContent={() => {
              return (
                <Text color="white">
                  <>
                    {formatTooltipValue(displayData.value)}
                  </>
                </Text>
              );
            }}
          />
        )}

        <StyledValueText
          data-tip
          data-for={`${widgetId}-header-value-tool-tip`}
          className={`${fontSize} value`}
        >
          <>
            {formatValue(displayData.value)}
          </>
        </StyledValueText>

        {displayData.total ? (
          <StyledTotalText className={`${fontSize} units`}>
            {`${formatNumber(displayData.total)} ${units}`}
          </StyledTotalText>
        ) : (
          <StyledTotalText className={`${fontSize} units`}>
            {units}
          </StyledTotalText>
        )}

      </StyledNumbersWrapper>

      {displayData.data && (
        <StyledTableWrapper>
          <table>
            {headers && (
              <thead>
                <tr>
                  {headers.map(header => (
                    <td key={header}>
                      <Text className="headers">
                        {header}
                      </Text>
                    </td>
                  ))}
                </tr>
              </thead>
            )}

            <tbody>
              {displayData.data.map((item, iteration) => (

                <tr key={item.group}>
                  {hideToolTip === true || hideToolTip !== "td" && (
                    <Tooltip
                      id={`${iteration}-${item.value}-tool-tip`}
                      type="dark"
                      place="top"
                      getContent={() => {
                        return (
                          <Text color="white">
                            <>
                              {formatTooltipValue(item.value)}
                            </>
                          </Text>
                        );
                      }}
                    />
                  )}

                  <td>
                    <Text>
                      {item.group}
                    </Text>
                  </td>

                  <td
                    data-tip
                    data-for={`${iteration}-${item.value}-tool-tip`}
                  >
                    <Text className="number">
                      <>
                        {formatValue(item.value)}
                      </>
                    </Text>

                  </td>

                  {item.total && (
                    <td>
                      <Text className="number">
                        {formatNumber(item.total)}
                      </Text>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </StyledTableWrapper>
      )}
    </>

  );
};

const StyledNumbersWrapper = styled.div`${({ theme }) => css`
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.spacing.margins.sm}
`}`;

const StyledValueText = styled(Text)`${({ theme }) => css`
  color: ${theme.colors.primaryDark};

  &.xs {
    font-size: ${theme.fonts.sizes.h3};
  }

  &.sm {
    font-size: ${theme.fonts.sizes.h2};
  }

  &.md {
    font-size: ${theme.fonts.sizes.h1};
  }

  &.lg {
    font-size: 40px;
  }

  &.xl {
    font-size: 60px;
  }

  &.xxl {
    font-size: 90px;
  }
`}`;

const StyledTotalText = styled(Text)`${({ theme }) => css`
  color: ${theme.colors.primaryDark};

  &.xs {
    font-size: ${theme.fonts.sizes.h6};
  }

  &.sm {
    font-size: ${theme.fonts.sizes.h5};
  }

  &.md {
    font-size: ${theme.fonts.sizes.h4};
  }

  &.lg {
    font-size: ${theme.fonts.sizes.h3};
  }

  &.xl {
    font-size: ${theme.fonts.sizes.h2};
  }

  &.xxl {
    font-size: ${theme.fonts.sizes.h1};
  }
`}`;

const StyledTableWrapper = styled.div`${({ theme }) => css`
  table {
    padding: ${theme.spacing.padding.xs};
    width: 100%;
  }

  td {
    padding: ${theme.spacing.padding.xs};
  }

  tbody {
    tr {
      border-top: solid 1px ${theme.colors.lightGrey};
      border-color: ${theme.colors.lightGrey};
    }
  }

  .headers {
    display: flex;
    justify-content: center;
    font-weight: ${theme.fonts.weights.bold};
    
  }

  .number {
    display: flex;
    justify-content: right;
  }
`}`;
